import { motion } from 'framer-motion';
import { useMemo } from 'react';

// Large ICON MARK
const LOGO_ICON = 'https://customer-assets.emergentagent.com/job_87ca2ea8-c6ba-444e-8df8-183282805244/artifacts/ggwojsj0_Desktop%20-%206.png';

// Generate random star positions
function generateStars(count) {
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      opacity: 0.15 + Math.random() * 0.35,
      size: 1 + Math.random() * 1.5,
      twinkle: Math.random() > 0.6,
      delay: Math.random() * 5,
    });
  }
  return stars;
}

// Star particles
function StarParticles() {
  const stars = useMemo(() => generateStars(60), []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {stars.map((star) => (
        <div
          key={star.id}
          className={`absolute rounded-full bg-white ${star.twinkle ? 'animate-twinkle' : ''}`}
          style={{
            top: `${star.top}%`,
            left: `${star.left}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.opacity,
            animationDelay: star.twinkle ? `${star.delay}s` : undefined,
          }}
        />
      ))}
    </div>
  );
}

// Gradient orbs for ambient lighting
function GradientOrbs() {
  return (
    <>
      {/* Top left orb */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-20%',
          left: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 60%)',
          filter: 'blur(60px)',
        }}
      />
      {/* Top right orb */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-15%',
          right: '-10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 60%)',
          filter: 'blur(50px)',
        }}
      />
      {/* Bottom center glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          bottom: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '120%',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 30%, transparent 60%)',
        }}
      />
    </>
  );
}

// Subtle grid pattern
function GridPattern() {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)
        `,
        backgroundSize: '80px 80px',
        maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        opacity: 0.5,
      }}
    />
  );
}

// Animated ring around logo
function LogoRing() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
        className="absolute w-[160px] h-[160px] rounded-full"
        style={{
          border: '1px dashed rgba(255,255,255,0.1)',
        }}
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
        className="absolute w-[200px] h-[200px] rounded-full"
        style={{
          border: '1px solid rgba(255,255,255,0.05)',
        }}
      />
    </div>
  );
}

export default function Hero() {
  return (
    <section 
      id="hero" 
      className="relative bg-black overflow-hidden"
      style={{ height: '100vh', minHeight: '700px' }}
      data-testid="hero-section"
    >
      {/* Background effects */}
      <GradientOrbs />
      <GridPattern />
      <StarParticles />

      {/* Main content - centered with slight offset for visual balance */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 pt-16">
        <div className="max-w-[700px] mx-auto text-center">
          
          {/* Logo with animated ring - positioned with proper centering and lower placement */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="relative mb-10 mx-auto"
            style={{ width: '120px', height: '120px' }}
          >
            <LogoRing />
            <img
              src={LOGO_ICON}
              alt="AIBL"
              className="w-full h-full object-contain relative z-10"
              style={{ filter: 'brightness(1.1)' }}
              data-testid="hero-icon"
            />
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6"
          >
            <span 
              className="inline-block text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60 px-4 py-2 rounded-full"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              The Liquidity Layer for Autonomous Agents
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="text-[48px] sm:text-[64px] md:text-[80px] font-black tracking-[-0.04em] leading-[0.95] text-white mb-6"
          >
            DeFi for
            <br />
            <span className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text">
              AI agents.
            </span>
          </motion.h1>

          {/* Subline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-[17px] sm:text-[18px] text-white/50 leading-[1.7] max-w-[540px] mx-auto mb-12"
          >
            One API to connect any autonomous agent to billions in on-chain liquidity. Multi-chain payments, lending, and DeFi across 20+ chains.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button 
              className="group relative bg-white text-black font-bold text-[14px] px-8 py-4 rounded-full hover:bg-white/90 transition-all overflow-hidden"
              data-testid="hero-cta-primary"
            >
              <span className="relative z-10">Get Started</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <button 
              className="text-white/80 font-medium text-[14px] px-8 py-4 rounded-full border border-white/15 hover:border-white/30 hover:text-white transition-all backdrop-blur-sm"
              data-testid="hero-cta-secondary"
            >
              Read Documentation
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
